import { useAtomValue } from "jotai";
import isString from "lodash/isString";
import type { NextPageContext, PageProps } from "next";
import { memo } from "react";
import { graphql, usePreloadedQuery } from "react-relay";
import { withRelay } from "relay-nextjs";
import type { RelayComponent } from "relay-nextjs";

import type { AppBarVariant } from "~/components/app-bar/helpers";
import { useAppBarAdSlot } from "~/components/app-bar/hooks";
import type { Application } from "~/components/app-initializer/helpers";
import { ArticleLinkingData } from "~/components/article/article-linking-data";
import { ArticleList } from "~/components/article/article-list";
import { DefaultCustomContents } from "~/components/article/article-render/consts";
import { useArticleBaseAdvertisingInfo } from "~/components/article/article-render/hooks";
import { PlusCustomContents } from "~/components/article/article-render/plus/consts";
import { StyleCustomContents } from "~/components/article/article-render/style-article/consts";
import { ArticleSchema } from "~/components/article/article-schema";
import { ArticleSeo } from "~/components/article/article-seo";
import { FallbackLoading } from "~/components/fallback-loading";
import { currentArticleAtom } from "~/lib/current-item";
import { createClientEnvironment } from "~/lib/relay/environment.client";
import type { articlePageQuery } from "~/queries/__generated__/articlePageQuery.graphql";

import {
  useArticleAcknowledgementGateOverlay,
  useTrackContentInterest,
  useTrackPageView,
} from "./hooks";
import { Article } from "./styles";

export const query = graphql`
  query articlePageQuery(
    $entityId: String!
    $entityUuid: String!
    $customContents: [CustomContentInput]
  ) {
    article(filter: { entityId: $entityId }) {
      ...articleBaseAdvertisingInfoArticle
      ...articleListArticle @arguments(customContents: $customContents)
      ...articleSeoArticle @arguments(customContents: $customContents)
      ...helpersCheckIsPostiesArticle
      ...hooksArticleAcknowledgementGateOverlayArticle
      ...helpersCheckIsPostiesArticle
      ...hooksTrackPageViewArticle
      ...articleLinkingDataContent
      entityId
    }
    ...articleSchemaContent @arguments(entityId: $entityId, entityUuid: $entityUuid)
  }
`;

const ArticlePage: RelayComponent<PageProps, articlePageQuery> = ({
  appBarConfiguration,
  isBot,
  preloadedQuery,
}) => {
  const data = usePreloadedQuery(query, preloadedQuery);
  const advertisingInfo = useArticleBaseAdvertisingInfo(data.article);
  const currentArticle = useAtomValue(currentArticleAtom);
  const { isAcknowledgementRequired, renderAcknowledgementGateOverlay } =
    useArticleAcknowledgementGateOverlay(data.article);

  useTrackContentInterest(currentArticle);
  useTrackPageView(currentArticle ?? data.article);
  useAppBarAdSlot({
    desktop: null,
    mobile: appBarConfiguration?.hasMobileAd
      ? {
          ...advertisingInfo,
          adUnit: "m_banner3",
          autoRefreshOptions: {
            scrollBackRefreshOptions: {
              slotArticleEntityId: data.article.entityId,
            },
          },
        }
      : null,
  });

  return (
    <>
      <ArticleSeo reference={data.article} />
      <ArticleLinkingData reference={data.article} />
      <ArticleSchema reference={data} />
      {renderAcknowledgementGateOverlay()}
      {/* TODO: workaround for fixing https://scmp-product-tech.atlassian.net/browse/SCMPWEB-3430, third party should not relied on the application level */}
      <Article $isShowArticle={!isAcknowledgementRequired} className="article-list">
        <List isBot={isBot === "1"} reference={data.article} />
      </Article>
    </>
  );
};

const List = memo(ArticleList);

ArticlePage.displayName = "ArticlePage";

const getHasMobileAd = (application: Application) => {
  switch (application) {
    case "posties":
      return false;
    default:
      return true;
  }
};

const getPageProps = (context: NextPageContext) => {
  const appBarVariant = context.query.appBarVariant as AppBarVariant;
  const application = context.query.application as Application;
  const isBot = context.query.isBot as string;

  const pageProps: PageProps = {
    animateKey: `article-${context.query.entityId?.toString() ?? ""}`,
    appBarConfiguration: {
      hasMobileAd: getHasMobileAd(application),
      variant: appBarVariant,
    },
    appInitializerConfiguration: {
      application,
    },
    contentConfiguration: {
      responsivePadding: {
        desktopUp: 32,
        mediumDesktopUp: 52,
        tabletUp: 12,
      },
    },
    headerConfiguration: {
      responsiveFeatures: {
        mobileUp: {
          default: ["dashboard"],
          swapped: ["article-speech", "content-comment", "content-share", "content-bookmark"],
        },
      },
      responsiveLogoVariant: {
        mobileUp: {
          default: "full",
          swapped: "logo",
        },
      },
    },
    isBot,
  };

  if (appBarVariant === "scmp/magazines-style") {
    pageProps.pageConfiguration = {
      ...pageProps.pageConfiguration,
      responsivePadding: {
        desktopUp: 40,
        mobileUp: 16,
        tabletUp: 20,
      },
    };
    pageProps.contentConfiguration = {
      ...pageProps.contentConfiguration,
      responsivePadding: {
        desktopUp: 135,
        mobileUp: 24,
        tabletUp: 70,
      },
    };
  }

  return pageProps;
};

export default withRelay(ArticlePage, query, {
  clientSideProps: context => getPageProps(context),
  createClientEnvironment: () => createClientEnvironment(),
  createServerEnvironment: async context => {
    const { createServerEnvironment } = await import("~/lib/relay/environment.server");
    return createServerEnvironment(context.req?.cookies);
  },
  fallback: <FallbackLoading />,
  serverSideProps: context => Promise.resolve(getPageProps(context)),
  variablesFromContext(context) {
    const appBarVariant = context.query.appBarVariant as AppBarVariant;
    const getCustomContents = () => {
      switch (appBarVariant) {
        case "scmp/magazines-style":
          return StyleCustomContents;
        case "scmp/plus":
          return PlusCustomContents;
        default:
          return DefaultCustomContents;
      }
    };

    return isString(context.query.entityId) && isString(context.query.entityUuid)
      ? {
          customContents: getCustomContents(),
          entityId: context.query.entityId,
          entityUuid: context.query.entityUuid,
        }
      : { customContents: [], entityId: "", entityUuid: "" };
  },
});
